import React from "react";
import { Box, Flex } from "@chakra-ui/react";
import { Outlet, useLocation } from "react-router-dom";
import TopNavBar from "../components/TopNavBar";
import BottomNavBar from "../components/BottomNavBar";
import UserFooter from "../components/UserFooter";
import PolicyConsentGate from "../components/PolicyConsentGate";
import LegalReconsentGate from "../components/legal/LegalReconsentGate";
import PaidChangeConsentGate from "../components/legal/PaidChangeConsentGate";
import TierRolloutInterstitial from "../components/tier/TierRolloutInterstitial";
import PartnerLayout from "./PartnerLayout";
import { useUser } from "../contexts/UserContext";
import { usesPartnerLayout } from "../lib/partnerNav";
import LayWidget from "../components/lay/LayWidget";
import OpsChatLauncher from "../components/opsChat/OpsChatLauncher";
import useSupportInquiryVisible from "../hooks/useSupportInquiryVisible";
import NaverRevenueGate from "../components/naver/NaverRevenueGate";
import SignupLockRedirect from "../components/billing/SignupLockRedirect";
import WelcomeRedirect from "../components/welcome/WelcomeRedirect";
import AnnouncementGate from "../components/announcement/AnnouncementGate";

const UserLayout = () => {
  const { userInfo } = useUser();
  const location = useLocation();
  // 푸터 「문의하기」 — 2026-11-01 0시(KST)부터 · 그 전에는 어드민 「공지」 화면 스위치가 켜졌을 때만(어드민 미리보기) · 2026-10-23 대표 지시
  const supportOpen = useSupportInquiryVisible(userInfo?.role);

  // 파트너 포털 레이아웃 — 좌측 레일 + 상단 스트립 (2026-09-10 N3). 라우트·ProtectedRoute 는 그대로이고 겉모습만 바뀐다.
  // 판정은 역할 OR 경로(2026-09-11, 대표 결정 ④): Partner 단독은 어디서든, Admin 겸직은 /partner* 에서만(「어드민 확인용」 띠) —
  // 대표·운영이 보는 화면이 파트너가 보는 화면과 같아야 한다. 종전엔 겸직이 크리에이터 상단바 아래에서 파트너 홈을 봤다.
  if (usesPartnerLayout(userInfo?.role, location.pathname)) return <PartnerLayout />;

  return (
    <Flex direction="column" minH="100dvh" bg="neutral.50">
      <TopNavBar />
      {/* 숏폼 정책 차단형 동의 팝업 — policy.is_active 전에는 아무것도 렌더하지 않는다 */}
      <PolicyConsentGate />
      {/* 약관·방침 v3 재동의 + 만 14세 확인 — policy.version 상향(10/01 activate-legal-v3.sql) 전에는 아무것도 렌더하지 않는다 */}
      <LegalReconsentGate />
      {/* 유료 전환 별도 동의(전자상거래법 §13⑥) — 10/1~10/31 창, 멤버십 구독료 등급 기존 회원만 */}
      <PaidChangeConsentGate />
      {/* 등급제 배포 안내 — 첫 로그인 전면 안내 1회(대표 결정 2026-09-07 · 시안 C). 서버에 확인 기록이 있으면 아무것도 렌더하지 않는다 */}
      {/* 「새로운 레이블리」 환영 여정 — app_config.welcome_2026 스위치가 켜지고 아직 「동의하고 시작」 전이면 /welcome 으로(위 게이트 4개는 안전망으로 그대로) */}
      <WelcomeRedirect />
      <TierRolloutInterstitial />
      {/* 타깃 공지 전면 안내(2026-10-28) — 운영팀이 「로그인하면 전면 안내」로 보낸 공지를 「확인했습니다」 전까지 띄운다. 다른 전면 안내가 닫힌 뒤에 뜬다 */}
      <AnnouncementGate />
      {/* 네이버 클립 수익 정책 ③ 게이트(대표 결정 2026-09-22) — 마감 넘긴 미제출 카드가 있으면 제출 전까지 전면 차단. 서버 my_naver_revenue_gate 가 false 면 아무것도 렌더하지 않는다. Admin·Platform 제외 */}
      {userInfo?.email && userInfo?.role !== "Admin" && userInfo?.role !== "Platform" && <NaverRevenueGate email={userInfo.email} />}
      {/* 정식 등급이 되는 순간(시작 등급 지정 · 루키 승급 심사 승인) 가입비 결제 + 카드 등록 전 잠금(대표 지시 2026-10-20 · 시안 C) —
          「레이블리 시작하기」(/start)로 보낸다. 서버 tier_grant_payment_gate 가 gated=false 면 아무것도 하지 않는다(대부분의 회원). Admin·Platform 제외 */}
      {userInfo?.email && userInfo?.role !== "Admin" && userInfo?.role !== "Platform" && <SignupLockRedirect email={userInfo.email} />}
      {/* 2026-08-30(모바일 추이 차트 버그 수정): 이 Box는 위 <Flex direction="column"> 의 flex
          아이템이라 기본적으로 min-width:auto(내용 기반 하한)를 갖는다 — 이 상태에서 자식 어딘가에
          가로 스크롤 폭을 고정 px로 강제하는 요소(예: MyTier 모바일 추이 스파크라인의 넓은 SVG)가
          있으면, 그 폭이 이 Box(→ 결국 body)까지 그대로 번져 페이지 전체가 옆으로 밀린다.
          `minW={0}`을 명시해 이 flex 아이템이 내용 크기와 무관하게 자기 몫(w="100%")까지만
          줄어들도록 한다 — Outlet 안 페이지 하나가 넓은 콘텐츠를 담아도 이 레이아웃 자체는 항상
          뷰포트 폭을 지킨다. 다른 페이지 렌더에는 영향 없음(원래도 콘텐츠가 좁으면 아무 차이 없음). */}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        minH={0}
        minW={0}
        p={{ base: 4, md: 8 }}
        pb={{ base: 0, md: 8 }}
        maxW={{ base: "100%", md: "1200px", xl: "1400px" }}
        w="100%"
        mx={{ base: 0, md: "auto" }}
      >
        <Outlet />
      </Box>
      <UserFooter supportLink={supportOpen.visible} />
      <BottomNavBar />
      {/* 레이 AI 도우미 — 우측 하단 런처 (2026-09-09, 시안 2 「레이 설정」 시트). 데모 빌드에서는 REACT_APP_LAY_WIDGET=0 로 숨긴다 — docs/pages/main/LayWidget.md */}
      {process.env.REACT_APP_LAY_WIDGET !== "0" && <LayWidget />}
      {/* 운영팀 대화방 — 운영팀이 연 방이 있을 때만 레이 버튼 바로 위에 뜬다(2026-10-23 · docs/ops/ops-chat-room-plan.md) */}
      {userInfo?.email && userInfo?.role !== "Platform" && <OpsChatLauncher />}
    </Flex>
  );
};

export default UserLayout;
